import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { StatusBadge } from '../components/ui/StatusBadge';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { Sheet, SheetContent, SheetBody, SheetFooter } from '../components/ui/Sheet';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMoney, formatNumber } from '../lib/format';
import { AUTO_LABEL_NAMES } from '../lib/automationLabels';
import type { AutoLabel, LabelSettings, LabelsOverviewResponse } from '../types/api';

function labelDescription(label: AutoLabel, settings: LabelSettings): string {
  switch (label) {
    case 'new_customer':
      return 'First order within the last 30 days.';
    case 'repeat':
      return '2 completed orders.';
    case 'loyal':
      return `${settings.loyalOrderCount} or more completed orders.`;
    case 'platinum':
      return `Total order value from ${formatMoney(settings.platinumThresholdAmount, settings.currency)} up to the VIP threshold.`;
    case 'vip':
      return `Total order value of ${formatMoney(settings.vipThresholdAmount, settings.currency)} or more.`;
    case 'inactive':
      return `No purchase within the last ${settings.inactiveAfterDays} days.`;
    case 'lost':
      return `No purchase within the last ${settings.lostAfterDays} days.`;
    case 'subscriber_only':
      return 'Subscribed to the newsletter, but has never bought anything.';
    default:
      return '';
  }
}

/** Labels whose threshold is fixed — the edit sheet shows the rule but no editable field. */
const FIXED_LABELS = new Set<AutoLabel>(['new_customer', 'repeat', 'subscriber_only']);

const LABEL_ORDER: AutoLabel[] = [
  'new_customer',
  'repeat',
  'loyal',
  'platinum',
  'vip',
  'inactive',
  'lost',
  'subscriber_only',
];

function LabelEditForm({
  label,
  form,
  isAdmin,
  pending,
  onChange,
  onCancel,
  onSave,
}: {
  label: AutoLabel;
  form: LabelSettings;
  isAdmin: boolean;
  pending: boolean;
  onChange: <K extends keyof LabelSettings>(key: K, value: LabelSettings[K]) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const editable = !FIXED_LABELS.has(label);
  // Platinum is the band below VIP, so the server refuses a platinum
  // threshold at or above the VIP one (apps/app/src/routes/labels.ts).
  // Checked on every sheet: each save sends all settings.
  const bandInvalid = form.platinumThresholdAmount >= form.vipThresholdAmount;
  return (
    <>
      <SheetBody>
        <p className="text-sm m-0">{labelDescription(label, form)}</p>
        {!isAdmin ? (
          <Alert>
            You have read-only access. Contact an administrator to change the thresholds.
          </Alert>
        ) : null}

        {label === 'loyal' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lbl-loyal">Loyal customer from (number of orders)</Label>
            <Input
              id="lbl-loyal"
              type="number"
              min={1}
              disabled={!isAdmin}
              value={form.loyalOrderCount}
              onChange={(e) => onChange('loyalOrderCount', Number(e.target.value))}
            />
          </div>
        ) : null}

        {label === 'platinum' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lbl-platinum">Platinum threshold ({form.currency})</Label>
            <Input
              id="lbl-platinum"
              type="number"
              min={0}
              disabled={!isAdmin}
              value={form.platinumThresholdAmount}
              onChange={(e) => onChange('platinumThresholdAmount', Number(e.target.value))}
            />
            <p className="text-xs text-muted m-0">
              Must be below the VIP threshold ({formatMoney(form.vipThresholdAmount, form.currency)}
              ). VIP customers are never also Platinum.
            </p>
          </div>
        ) : null}

        {label === 'vip' ? (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="lbl-vip">VIP threshold ({form.currency})</Label>
              <Input
                id="lbl-vip"
                type="number"
                min={0}
                disabled={!isAdmin}
                value={form.vipThresholdAmount}
                onChange={(e) => onChange('vipThresholdAmount', Number(e.target.value))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="lbl-currency">Currency</Label>
              <Input
                id="lbl-currency"
                disabled={!isAdmin}
                value={form.currency}
                maxLength={3}
                onChange={(e) => onChange('currency', e.target.value.toUpperCase())}
              />
            </div>
          </>
        ) : null}

        {label === 'inactive' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lbl-inactive">Inactive after (days)</Label>
            <Input
              id="lbl-inactive"
              type="number"
              min={1}
              disabled={!isAdmin}
              value={form.inactiveAfterDays}
              onChange={(e) => onChange('inactiveAfterDays', Number(e.target.value))}
            />
          </div>
        ) : null}

        {label === 'lost' ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lbl-lost">Lost after (days)</Label>
            <Input
              id="lbl-lost"
              type="number"
              min={1}
              disabled={!isAdmin}
              value={form.lostAfterDays}
              onChange={(e) => onChange('lostAfterDays', Number(e.target.value))}
            />
          </div>
        ) : null}

        {bandInvalid ? (
          <Alert>The Platinum threshold must be below the VIP threshold.</Alert>
        ) : null}

        {!editable ? (
          <p className="text-xs text-muted m-0">This condition is fixed and cannot be adjusted.</p>
        ) : null}
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" className="flex-grow" onClick={onCancel}>
          Cancel
        </Button>
        {isAdmin && editable ? (
          <Button className="flex-grow" disabled={pending || bandInvalid} onClick={onSave}>
            {pending ? 'Saving…' : 'Save changes'}
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

export function KundemaerkaterPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['labels'],
    queryFn: () => apiFetch<LabelsOverviewResponse>('/labels'),
  });

  const [form, setForm] = useState<LabelSettings | null>(null);
  useEffect(() => {
    if (data?.settings) setForm(data.settings);
  }, [data?.settings]);

  // `data.counts` is an array (see types/api.ts#LabelsOverviewResponse) and
  // only has a row for a label with at least one contact — look up by
  // label, defaulting a missing one to 0 rather than indexing directly.
  const countByLabel = new Map((data?.counts ?? []).map((c) => [c.label, c.count]));

  const [editing, setEditing] = useState<AutoLabel | null>(null);
  const autoNames = new Set<string>(LABEL_ORDER);
  const manualCounts = (data?.counts ?? [])
    .filter((c) => !autoNames.has(c.label))
    .sort((a, b) => a.label.localeCompare(b.label));

  const saveMutation = useMutation({
    mutationFn: (next: LabelSettings) => {
      const { updatedAt: _updatedAt, ...body } = next;
      // The server answers with the bare settings object (apps/app/src/routes/labels.ts).
      return apiFetch<LabelSettings>('/labels/settings', { method: 'PUT', body });
    },
    onSuccess: (settings) => {
      setForm(settings);
      void queryClient.invalidateQueries({ queryKey: ['labels'] });
      setEditing(null);
    },
  });

  // Closing without saving drops the unsaved values, so they can't ride
  // along with the next save from another tag's sheet.
  function closeSheet() {
    setForm(data?.settings ?? null);
    setEditing(null);
  }

  function update<K extends keyof LabelSettings>(key: K, value: LabelSettings[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  if (isLoading || !form || !data) return <div className="text-muted">Loading tags…</div>;

  return (
    <>
      <PageHeader
        title="Customer tags"
        badge={<MockBadge />}
        description="Automatic tags are continuously recalculated from the thresholds below. Manual tags are added and removed under Tags on each contact's page."
      />

      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs text-muted uppercase tracking-wide">
              <th className="px-4 py-2.5 font-medium">Tag</th>
              <th className="px-4 py-2.5 font-medium">Condition</th>
              <th className="px-4 py-2.5 font-medium text-right">Contacts</th>
              <th className="px-4 py-2.5 font-medium" />
            </tr>
          </thead>
          <tbody>
            {LABEL_ORDER.map((label) => (
              <tr key={label} className="border-t border-border-soft">
                <td className="px-4 py-3">
                  <button type="button" onClick={() => setEditing(label)} className="text-left">
                    <StatusBadge tone="accent" icon="●">
                      {AUTO_LABEL_NAMES[label]}
                    </StatusBadge>
                  </button>
                </td>
                <td className="px-4 py-3 text-muted">{labelDescription(label, form)}</td>
                <td className="px-4 py-3 text-right font-mono">
                  {formatNumber(countByLabel.get(label) ?? 0)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/kontakter?label=${label}`} className="text-link text-sm">
                    View contacts
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section aria-labelledby="manual-tags-heading" className="flex flex-col gap-2">
        <h2 id="manual-tags-heading" className="text-sm font-semibold m-0">
          Manual tags
        </h2>
        {manualCounts.length === 0 ? (
          <p className="text-sm text-muted m-0">
            No manual tags yet. Add one under Tags on a contact&apos;s page.
          </p>
        ) : (
          <div className="border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs text-muted uppercase tracking-wide">
                  <th className="px-4 py-2.5 font-medium">Tag</th>
                  <th className="px-4 py-2.5 font-medium text-right">Contacts</th>
                  <th className="px-4 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {manualCounts.map((c) => (
                  <tr key={c.label} className="border-t border-border-soft">
                    <td className="px-4 py-3">
                      <StatusBadge tone="neutral" icon="●">
                        {c.label}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{formatNumber(c.count)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/kontakter?label=${encodeURIComponent(c.label)}`}
                        className="text-link text-sm"
                      >
                        View contacts
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Card className="p-4 flex flex-col gap-3 max-w-[560px]">
        <h2 className="text-sm font-semibold m-0">General consent settings</h2>
        {!isAdmin ? (
          <Alert>You have read-only access. Contact an administrator to change the settings.</Alert>
        ) : null}
        <div className="flex flex-col gap-1.5 max-w-[220px]">
          <Label htmlFor="lbl-softoptin">Soft consent for up to (days)</Label>
          <Input
            id="lbl-softoptin"
            type="number"
            min={1}
            disabled={!isAdmin}
            value={form.softOptinMaxDays}
            onChange={(e) => update('softOptinMaxDays', Number(e.target.value))}
          />
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={!isAdmin}
              checked={form.singleOptinCountsAsOptin}
              onChange={(e) => update('singleOptinCountsAsOptin', e.target.checked)}
            />
            Single opt-in counts as valid consent
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={!isAdmin}
              checked={form.timeOnlyCountsAsOptin}
              onChange={(e) => update('timeOnlyCountsAsOptin', e.target.checked)}
            />
            Time alone (without interaction) counts as valid consent
          </label>
        </div>
        {isAdmin ? (
          <Button
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate(form)}
            className="self-end"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        ) : null}
        {saveMutation.isSuccess && !editing ? (
          <div role="status" className="text-success-text text-sm">
            Saved.
          </div>
        ) : null}
        {saveMutation.isError ? (
          <Alert>The settings could not be saved. Please try again.</Alert>
        ) : null}
      </Card>

      <Sheet
        open={Boolean(editing)}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
      >
        {editing ? (
          <SheetContent
            eyebrow="Edit tag"
            title={AUTO_LABEL_NAMES[editing]}
            description={`Edit the condition for the ${AUTO_LABEL_NAMES[editing]} tag.`}
          >
            <LabelEditForm
              label={editing}
              form={form}
              isAdmin={isAdmin}
              pending={saveMutation.isPending}
              onChange={update}
              onCancel={closeSheet}
              onSave={() => saveMutation.mutate(form)}
            />
          </SheetContent>
        ) : null}
      </Sheet>
    </>
  );
}
